// USGS earthquake feed → nearby quakes.

import { haversineKm } from "../weather";
import type { Quake } from "./types";

/** Minimal shape of a USGS GeoJSON summary-feed feature. */
export interface UsgsFeature {
  id?: string;
  properties?: {
    mag?: number | null;
    place?: string | null;
    time?: number | null;
    url?: string | null;
    tsunami?: number | null;
    alert?: string | null;
    type?: string | null;
    title?: string | null;
  };
  geometry?: { coordinates?: [number, number, number?] | number[] } | null;
}

export const QUAKE_LIMITS = { radiusKm: { min: 50, max: 2000 }, minMag: { min: 2.5, max: 7 } };

/** Quakes within `radiusKm` of home at or above `minMag`, newest first. */
export function nearbyQuakes(features: UsgsFeature[], home: { lat: number; lon: number }, radiusKm: number, minMag: number, limit = 40): Quake[] {
  const out: Quake[] = [];
  for (const f of features) {
    const p = f.properties;
    const c = f.geometry?.coordinates;
    if (!p || !c || typeof f.id !== "string") continue;
    const [lon, lat, depth] = c as number[];
    const mag = p.mag;
    if (typeof mag !== "number" || !Number.isFinite(mag) || mag < minMag) continue;
    if (typeof lat !== "number" || typeof lon !== "number" || typeof p.time !== "number") continue;
    const distanceKm = haversineKm(home.lat, home.lon, lat, lon);
    if (distanceKm > radiusKm) continue;
    out.push({
      id: f.id,
      mag: Math.round(mag * 10) / 10,
      place: (p.place || p.title || "Unknown location").slice(0, 120),
      time: new Date(p.time).toISOString(),
      lat,
      lon,
      depthKm: typeof depth === "number" && Number.isFinite(depth) ? depth : null,
      distanceKm: Math.round(distanceKm),
      url: typeof p.url === "string" && p.url.startsWith("https://earthquake.usgs.gov/") ? p.url : null,
      tsunami: p.tsunami === 1,
      alert: typeof p.alert === "string" ? p.alert : null,
    });
  }
  return out.sort((a, b) => b.time.localeCompare(a.time)).slice(0, limit);
}

/** Rough felt-ness wording by magnitude (USGS "magnitude classes"). */
export function quakeClass(mag: number): string {
  if (mag < 3) return "Minor · rarely felt";
  if (mag < 4) return "Minor · often felt";
  if (mag < 5) return "Light";
  if (mag < 6) return "Moderate";
  if (mag < 7) return "Strong";
  if (mag < 8) return "Major";
  return "Great";
}
